<template>
  <Modal
    ref="projectAccessModal"
    :title="'Project Access Control'"
    :width="'1200px'"
    @closed="resetData"
  >
    <template #modal-main>
      <div class="settings container">
        <div class="row mt-3">
          <div class="col-12 settings__control-group">
            <label class="label">Access Type</label>
            <div class="value permission-list">
              <div class="role-grid-single-row">
                <v-radio-group
                  v-model="tempShareMode"
                  :density="'compact'"
                  :hide-details="true"
                  @change="handleModeChange"
                >
                  <div class="chkbx-li mt-2">
                    <v-radio value="default" color="#250E81">
                      <template #label>
                        <div class="label-text">Organization</div>
                      </template>
                    </v-radio>
                    <span class="chkbx-li__subtitle">Everyone in your organization has access to this project.</span>
                  </div>
                  <div class="chkbx-li mt-2">
                    <v-radio value="public" color="#250E81">
                      <template #label>
                        <div class="label-text">Public</div>
                      </template>
                    </v-radio>
                    <span class="chkbx-li__subtitle">Share this project with EdgeFirst Studio community. Users outside your organization can read but not write to the project.</span>
                  </div>
                  <div class="chkbx-li mt-2">
                    <v-radio value="private" color="#250E81">
                      <template #label>
                        <div class="label-text">Private</div>
                      </template>
                    </v-radio>
                    <span class="chkbx-li__subtitle">Limit access to project owner and organization admins.</span>
                  </div>
                </v-radio-group>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 settings__control-group">
            <label class="label">Share with other users</label>
            <UserAccessList
              ref="userAccessList"
              :modalProject="modalProject"
              :shareMode="shareMode"
              @refresh-projects="$emit('refresh-projects')"
            />
          </div>
        </div>
        <div class="row mt-3" />
      </div>
    </template>
  </Modal>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="confirmButtonClass"
    :buttonText="confirmButtonText"
    @confirmed="confirmFunction"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import UserAccessList from '@/components/ProjectsComponent/UserAccessList.vue';

import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: 'ProjectAccessModal',
  components: {
    Modal,
    ConfirmModal,
    UserAccessList,
  },
  props: {
    modalProject: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['refresh-projects'],
  data() {
    return {
      dsConn: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      message: '',
      shareMode: '',
      tempShareMode: '',
      confirmMessage: null,
      confirmMessageHeader: null,
      confirmButtonText: '',
      confirmButtonClass: undefined,
      confirmFunction: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.user.user;
    },
  },
  watch: {
    modalProject: {
      immediate: true,
      handler() {
        if (this.modalProject) {
          if (this.modalProject.is_public) {
            this.shareMode = 'public';
          } else if (this.modalProject.permission_type) {
            this.shareMode = this.modalProject.permission_type;
          } else {
            this.shareMode = 'default';
          }
          this.tempShareMode = this.shareMode;
        }
      },
    },
  },
  methods: {
    showModal() {
      if (this.modalProject) {
        if (this.modalProject.is_public) {
          this.shareMode = 'public';
        } else if (this.modalProject.permission_type) {
          this.shareMode = this.modalProject.permission_type;
        } else {
          this.shareMode = 'default';
        }
        this.tempShareMode = this.shareMode;
      }
      this.$refs.projectAccessModal.showModal();
    },
    closeModal() {
      this.resetData();
    },
    resetData() {
      this.message = "";
      this.$refs.userAccessList?.reset();
    },
    handleModeChange(event) {
      const newDrawingMode = event.target.value;
      if (newDrawingMode === 'default') {
        this.confirmMessage = "Change access mode to Organization. All users in your organization will have access to this project.";
      } else if (newDrawingMode === 'private') {
        this.confirmMessage = "Change access mode to Private. Only the project owner and organization admins can access this project.";
      } else if (newDrawingMode === 'public') {
        this.confirmMessage = `Change access mode to Public. All users of EdgeFirst Studio can view this project but not edit it. 
        Editing is allowed for internal users of your organizations.`;
      }

      this.tempShareMode = this.shareMode;
      this.confirmMessageHeader = 'Access Change';
      this.confirmButtonText = 'Confirm';
      this.confirmButtonClass = undefined;
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => { this.confirmChanges(newDrawingMode); };
    },
    async confirmChanges(newDrawingMode) {
      this.message = "";
      const promises = [];
      this.shareMode = newDrawingMode;
      this.tempShareMode = newDrawingMode;
      if (this.shareMode === 'default') {
        promises.push(this.revertProjectAccessToDefault());
      } else if (this.shareMode === 'private') {
        promises.push(this.setProjectAccessToPrivate());
      } else if (this.shareMode === 'public') {
        promises.push(this.setProjectAccessToPublic());
      }

      await Promise.all(promises)
        .then(async () => {
          this.$refs.userAccessList?.refreshAccessList();
          this.$emit('refresh-projects');
        });
    },
    async revertProjectAccessToDefault() {
      await this.dsConn.revertProjectAccessToDefault({
        project_id: this.modalProject.id,
      })
        .then((data) => {
          if (!data.result || data.error) {
            this.message = data.error.message;
          } else {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Set organization access succesful.`,
              type: "success",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error setting organization access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    async setProjectAccessToPrivate() {
      await this.dsConn.setProjectAccessToPrivate({
        project_id: this.modalProject.id,
        username: this.currentUser.username,
      })
        .then((data) => {
          if (!data.result || data.error) {
            this.message = data.error.message;
          } else {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Set private access succesful.`,
              type: "success",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error setting private access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    async setProjectAccessToPublic() {
      await this.dsConn.setProjectAccessToPublic({
        project_id: this.modalProject.id,
      })
        .then((data) => {
          if (!data.result || data.error) {
            this.message = data.error.message;
          } else {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Set public access succesful.`,
              type: "success",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error setting public access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
  },
};

</script>

<style lang="scss" scoped>

.settings {
  padding: 20px;
  overflow: visible;
}

@media (min-width: $breakpoint-sm) {
  .settings {
    min-width: 480px;
  }
}

.italic {
  font-style: italic;
}

.removing {
  font-weight: 600;
  @include themify() {
    color: themed('color-red-600');
  }
}

.remove-row {
  justify-content: flex-end;
  &__button {
    font-weight: 600;
    font-size: 14px;
    width: fit-content;
    margin-right: 5px;
    border-radius: 20px;
    padding: 2px 5px 2px 5px;
    color: var(--color-red-600);
    border: 1px solid var(--color-red-600);

    &:hover{
      cursor: pointer;
      background: rgba(red, 0.1);
    }
  }
}

.row {
  flex-wrap: nowrap !important;
}

.row > .label {
  display: flex;
  justify-content: end;
  align-items: start;
  width: 130px;
  margin-top: 10px;
  text-align: right;
  font-size: 0.9rem;
  font-weight: 600;

}

.row > .value {
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  font-size: 0.9rem;
  margin-left: 20px;
}

.role-grid {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: auto;
  width: 100%;
}

.role-grid-single-row {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 1fr;
  grid-auto-rows: auto;

  .label-text {
    width: 200px;
  }
}

.permission-list {
  display: flex;
  flex-direction: column !important;
  width: 100%;

  &__title {
    margin: 5px 0 5px 0;
    font-size: 1rem;
    font-weight: 600;
    color: rgba(rgb(0, 0, 0), 0.7);
  }
}

.chkbx-li {
  display: flex;
  flex-direction: column;
  align-items: start;

  &__subtitle {
    text-align: left;
    margin-left: 30px;
    font-size: 0.85rem;
    color: rgba(grey, 0.95);
  }
}

.label-text {
  display: flex;
  flex-direction: row;
  cursor: pointer;
  align-items: center;
  color: var(--color-black) !important;
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: 2px;
  width: 100px;
}

:deep() {
  .v-label {
    opacity: 1 !important;
  }
}

.footer {
  display: flex;
  flex-direction: row;
  padding: 0.75rem 3rem;
  justify-content: flex-start;

  button + button {
    margin-left: 10px;
  }
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error')
  }
}

:deep {
  .click-container{
    z-index: 1000;
  }
  .v-field__input {
    overflow-x: auto;
    flex-wrap: nowrap;
  }
  .v-chip {
    overflow: initial;
  }
}

.button {
  background: white;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 32px;
  padding: 0;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
  padding: 4px 8px;
  gap: 2px;
  width: fit-content;

  @include themify() {
    color: themed('body-text-color')
  }

  &:hover {
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }

  &.delete:not([disabled]) {
    @include themify() {
      background: themed('button-delete');
      color: themed('button-delete-text');
    }
  }

  &[disabled] {
    border: none;
  }

  &.delete:hover:not([disabled]) {
    @include themify() {
      background: $color-red-300;
      color: themed('button-delete-text');
    }
  }
  }

.role-selector {
  height: 100%
}

.add-user-note {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  text-align: left;
  color: rgba(grey, 0.95);
}
</style>
